import { createAsyncThunk } from '@reduxjs/toolkit';
import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import { AxiosError, AxiosRequestConfig } from 'axios';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';

import apiClient from 'src/agent/api/apiClient';
import { getWeb3Signer, signRequest } from 'src/utils/web3Signer';

import type { ReduxDispatch, ReduxState } from '../types/common.type';

export const createAppAsyncThunk = createAsyncThunk.withTypes<{
  state: ReduxState;
  dispatch: ReduxDispatch;
  rejectValue: string;
}>();

export const useAppDispatch: () => ReduxDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<ReduxState> = useSelector;

interface TArgs {
  path: string;
  data?: AxiosRequestConfig['data'];
  method?: AxiosRequestConfig['method'];
  params?: AxiosRequestConfig['params'];
  headers?: AxiosRequestConfig['headers'];
  validateStatus?: AxiosRequestConfig['validateStatus'];
  shouldSign?: boolean;
}

export const axiosBaseQuery: BaseQueryFn<TArgs, unknown, unknown> = async ({
  path,
  method,
  data,
  params,
  headers,
  // validateStatus,
  shouldSign = false,
}) => {
  if (shouldSign) {
    const signer = getWeb3Signer();
    if (!signer) {
      throw new Error('Wallet not connected (signer missing)');
    }
    const { signature, issuedAt } = await signRequest(data);
    Object.assign(headers as any, {
      'x-offchain-signature': signature,
      'x-offchain-issued-at': issuedAt,
    });
  }

  return apiClient({ url: path, method, data, params, headers })
    .then(r => ({ data: r.data }))
    .catch((e: AxiosError) => {
      return {
        error: {
          status: e.response?.status,
          data: e.response?.data || e.message,
        },
      };
    });
};
